import React, { useEffect } from 'react';
import { Icon } from '@iconify/react';
import styles from './Drawer.module.scss';
import { useTheme } from '../../hooks/useTheme';

/**
 * Drawer Component - Painel Lateral Moderno
 * 
 * Componente profissional de painel lateral (sidebar/drawer) que desliza da direita.
 * Ideal para exibir detalhes, formulários longos e informações complementares.
 * 
 * @param {boolean} show - Controla visibilidade do drawer
 * @param {function} onHide - Callback ao fechar
 * @param {string} title - Título do drawer
 * @param {string} icon - Ícone do título (Iconify)
 * @param {string} size - Tamanho: 'sm' (400px), 'md' (600px), 'lg' (800px), 'xl' (1000px)
 * @param {ReactNode} footer - Conteúdo do rodapé (opcional)
 * @param {ReactNode} children - Conteúdo do corpo
 * @param {boolean} closeOnBackdrop - Fecha ao clicar no backdrop (padrão: true)
 * 
 * @example
 * <Drawer
 *   show={showDrawer}
 *   onHide={() => setShowDrawer(false)}
 *   title="Detalhes da Solicitação"
 *   icon="mdi:cash-multiple"
 *   size="md"
 *   footer={<Drawer.Button variant="primary">Salvar</Drawer.Button>}
 * >
 *   <Drawer.Section title="Informações">
 *     <p>Conteúdo aqui</p>
 *   </Drawer.Section>
 * </Drawer>
 */
const Drawer = ({
  show,
  onHide,
  title,
  icon,
  size = 'md',
  footer,
  children,
  closeOnBackdrop = true
}) => {
  const { theme } = useTheme();

  // Previne scroll do body quando drawer está aberto
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  // Fecha com ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && show) {
        onHide();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [show, onHide]);

  if (!show) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onHide();
    }
  };

  return (
    <div 
      className={styles.drawerBackdrop} 
      onClick={handleBackdropClick}
      data-theme={theme}
    >
      <div className={`${styles.drawerContainer} ${styles[size]}`}>
        {/* Header */}
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitle}>
            {icon && <Icon icon={icon} className={styles.titleIcon} />}
            <span>{title}</span>
          </div>
          <button 
            className={styles.closeButton}
            onClick={onHide}
            aria-label="Fechar"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.drawerBody}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={styles.drawerFooter}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Drawer.Button - Botão estilizado para o Drawer
 * 
 * @param {string} variant - Estilo: 'primary', 'secondary', 'success', 'danger', 'warning'
 * @param {boolean} loading - Estado de carregamento
 * @param {string} icon - Ícone (Iconify)
 * @param {function} onClick - Callback ao clicar
 * @param {boolean} disabled - Desabilita o botão
 * @param {string} className - Classes CSS adicionais
 */
Drawer.Button = ({ 
  children, 
  variant = 'primary', 
  onClick, 
  loading = false,
  icon,
  disabled = false,
  className = '',
  ...props 
}) => {
  return (
    <button
      type="button"
      className={`${styles.drawerButton} ${styles[variant]} ${className}`}
      onClick={onClick}
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <>
          <span className={`${styles.spinner} spinner-border spinner-border-sm me-2`} role="status" />
          Processando...
        </>
      ) : (
        <>
          {icon && <Icon icon={icon} className="me-2" />}
          {children}
        </>
      )}
    </button>
  );
};

/**
 * Drawer.Divider - Linha divisória
 */
Drawer.Divider = () => <div className={styles.drawerDivider} />;

/**
 * Drawer.Section - Seção com título opcional
 * 
 * @param {string} title - Título da seção
 * @param {string} icon - Ícone do título
 * @param {ReactNode} children - Conteúdo da seção
 */
Drawer.Section = ({ title, icon, children, className = '', style = {} }) => (
  <div className={`${styles.drawerSection} ${className}`} style={style}>
    {title && (
      <div className={styles.sectionHeader}>
        {icon && <Icon icon={icon} className={styles.sectionIcon} />}
        <span className={styles.sectionTitle}>{title}</span>
      </div>
    )}
    <div className={styles.sectionContent}>
      {children}
    </div>
  </div>
);

/**
 * Drawer.InfoCard - Card informativo
 * 
 * @param {string} variant - Estilo: 'info', 'success', 'warning', 'danger', 'neutral'
 * @param {string} icon - Ícone
 * @param {ReactNode} children - Conteúdo
 */
Drawer.InfoCard = ({ variant = 'info', icon, children, className = '' }) => (
  <div className={`${styles.infoCard} ${styles[variant]} ${className}`}>
    {icon && <Icon icon={icon} className={styles.infoIcon} />}
    <div className={styles.infoContent}>
      {children}
    </div>
  </div>
);

/**
 * Drawer.Row - Linha de informação (Label + Valor)
 * 
 * @param {string} label - Rótulo
 * @param {ReactNode} value - Valor
 * @param {boolean} highlight - Destaca a linha
 */
Drawer.Row = ({ label, value, highlight = false, className = '' }) => (
  <div className={`${styles.infoRow} ${highlight ? styles.highlight : ''} ${className}`}>
    <span className={styles.rowLabel}>{label}</span>
    <span className={styles.rowValue}>{value}</span>
  </div>
);

/**
 * Drawer.EmptyState - Estado vazio
 * 
 * @param {string} icon - Ícone
 * @param {string} text - Texto
 */
Drawer.EmptyState = ({ icon, text }) => (
  <div className={styles.emptyState}>
    <Icon icon={icon} className={styles.emptyIcon} />
    <p className={styles.emptyText}>{text}</p>
  </div>
);

/**
 * Drawer.Badge - Badge estilizado
 * 
 * @param {string} variant - Estilo: 'success', 'danger', 'warning', 'info', 'neutral'
 * @param {string} icon - Ícone
 * @param {ReactNode} children - Conteúdo
 */
Drawer.Badge = ({ variant = 'neutral', icon, children, className = '' }) => (
  <span className={`${styles.badge} ${styles[variant]} ${className}`}>
    {icon && <Icon icon={icon} className={styles.badgeIcon} />}
    {children}
  </span>
);

export default Drawer;

